import * as React from "react"
import Button from "@mui/material/Button"
import MenuItem from "@mui/material/MenuItem"
import AddIcon from "@mui/icons-material/Add"
import {
  Box,
  CircularProgress,
  IconButton,
  Select,
  TextField,
  Typography,
} from "@mui/material"

import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { extractObject, strFormat } from "./utils"
import { actionType } from "../../../pages/ResourceBudget/types"
import { useAppDispatch } from "../../../redux/hooks"
import {
  setConfirmClose,
  setConfirmLoading,
  setConfirmState,
} from "../../../redux/slices/forecastSlice"
import {
  deleteProvider,
  cancelNewEntry,
} from "../../../redux/slices/resourceBudgetSlice"
import { deleteForecastProvider } from "../../../redux/slices/forecastSlice"
import { colors } from "../../../../styles/commonStyles"
import { MRT_Cell } from "material-react-table"
import { formatNumber } from "../../../../helper/utils/formatting"

type addEntryProps = {
  appendHandler: any
  disable: boolean
  disabledReason?: string
}

type actionsFieldProps = {
  actions: actionType[]
  selectValues: string[]
  cell: MRT_Cell<any>
  updateHandler: any
  index: string
  fieldValue: string
  newEntry: boolean
  referenceData: {}[]
  defaultEditMode: boolean
}

type renderProps = {
  renderValue: string
  col?: string
  row?: any
  formatType?: string
  subtitle?: string
}

type tableSelectProps = {
  selectValues: string[]
  cell?: MRT_Cell<any>
  dispatch: any
  label: string
  index: string
  selectIndex?: string
  referenceData?: {}[]
  onProviderSelect?: (selected: any) => void
  newEntry?: boolean
  onCancel?: () => void
  isRoleCategory?: boolean
}

type tableTextFieldProps = {
  cell: MRT_Cell<any>
  dispatch: any
  label: string
  autoFocus?: boolean
  index: string
  onCommit?: (value: string) => void
}
export const AddNewEntry = ({
  appendHandler,
  disable,
  disabledReason,
}: addEntryProps) => {
  const dispatch = useAppDispatch()

  const addEntryHandler = () => {
    dispatch(appendHandler())
  }
  return (
    <Box
      sx={{
        position: "relative",
        height: "60px", // Match table row height
        display: "flex",
        alignItems: "center",
        padding: "8px 16px", // Add proper padding
        border: "0.5px solid #E4E4E4", // Match table cell border
        boxShadow: "#00000022 1.95px 0px 2.6px", // Match table cell shadow
      }}
    >
      <Button
        id="basic-button"
        onClick={addEntryHandler}
        disabled={disable}
        title={disable ? disabledReason : undefined}
        disableRipple
        disableElevation
        disableTouchRipple
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          textTransform: "none",
          fontSize: "14px",
          color: "#1976d2", // Blue color for the link
          padding: "0",
          minWidth: "auto",
          height: "auto",
          "&:hover": {
            backgroundColor: "transparent",
            textDecoration: "underline",
          },
        }}
      >
        <AddIcon sx={{ fontSize: "16px", mr: 1 }} />
        Add
      </Button>
    </Box>
  )
}

export const ActionAppendedField = (props: actionsFieldProps) => {
  const { updateHandler, actions, selectValues, cell, index } = props
  const { newEntry, referenceData, defaultEditMode, fieldValue } = props

  const dispatch = useAppDispatch()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [isProviderSelected, setIsProviderSelected] = React.useState(false)
  const autoSavedRef = React.useRef(false)
  const [shouldCancelEntry, setShouldCancelEntry] = React.useState(false)

  // Reset autoSavedRef when newEntry changes (new row added)
  React.useEffect(() => {
    if (newEntry) {
      autoSavedRef.current = false
      setIsProviderSelected(false)
    }
  }, [newEntry, cell?.row?.original?.id])

  const initialState = actions.reduce((accum: any, _, index) => {
    accum[index] = false
    return accum
  }, {})
  const [actionState, setActionState] = React.useState({
    state: { ...initialState },
    disabled: false,
  })
  const [isApiInProgress, setIsApiInProgress] = React.useState(false)

  const setLoading = (index: number) =>
    setActionState((prev) => ({
      state: { ...prev.state, [index]: true },
      disabled: true,
    }))

  const stopLoading = (index: number) =>
    setActionState((prev) => ({
      state: { ...prev.state, [index]: false },
      disabled: false,
    }))

  // Enhanced loading state management to prevent multiple API calls
  const handleApiCall = React.useCallback(
    async (
      apiCall: Function,
      payload: any,
      setSavedCallback: Function,
      setLoading: Function,
      stopLoading: Function,
    ) => {
      if (isApiInProgress) {
        console.warn("API call already in progress, ignoring duplicate request")
        return
      }

      setIsApiInProgress(true)
      try {
        // Use the payload as-is since refreshData is now properly passed from the action
        const enhancedPayload = payload

        await apiCall({
          ...enhancedPayload,
          setSaved: setSavedCallback,
          setLoading,
          stopLoading,
        })
      } catch (error) {
        console.error("API call failed:", error)
        // On error for a new entry row, remove the transient row immediately
        if (newEntry && cell?.row?.index === 1) {
          const isBudget = payload?.isBudget !== false
          if (isBudget) {
            dispatch(deleteProvider(1))
          } else {
            dispatch(deleteForecastProvider(1))
          }
        }
      } finally {
        setIsApiInProgress(false)
      }
    },
    [isApiInProgress, newEntry, cell?.row?.index],
  )

  // Handle Escape key and click outside to cancel new entry if no provider selected
  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && newEntry && !defaultEditMode) {
        // Check if a provider is selected
        const providerValue = cell?.row?.original?.[index]
        const hasValidProvider =
          providerValue &&
          providerValue !== `Select Provider` &&
          providerValue !== ""

        if (!hasValidProvider && !isProviderSelected) {
          // Cancel the new entry
          dispatch(cancelNewEntry())
        }
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        newEntry &&
        !defaultEditMode &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        // Check if click is inside the Material-UI Select menu (which is portaled to body)
        const target = event.target as HTMLElement
        const isInsideMenu = target.closest('.MuiPaper-root') &&
          (target.closest('.MuiMenu-paper') || target.closest('[role="listbox"]'))

        // Don't cancel if clicking inside the dropdown menu
        if (isInsideMenu) {
          return
        }

        // Use setTimeout to allow state updates from dropdown selection to complete
        setTimeout(() => {
          // Check if a provider is selected
          const providerValue = cell?.row?.original?.[index]
          const hasValidProvider =
            providerValue &&
            providerValue !== `Select Provider` &&
            providerValue !== ""

          if (!hasValidProvider && !isProviderSelected) {
            // Cancel the new entry
            dispatch(cancelNewEntry())
          }
        }, 200)
      }
    }

    if (newEntry && !defaultEditMode) {
      document.addEventListener("keydown", handleEscapeKey)
      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("keydown", handleEscapeKey)
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }
  }, [newEntry, defaultEditMode, cell, index, isProviderSelected, dispatch])

  // Track provider selection
  React.useEffect(() => {
    if (newEntry && !defaultEditMode) {
      const providerValue = cell?.row?.original?.[index]
      const hasValidProvider =
        providerValue &&
        providerValue !== `Select Provider` &&
        providerValue !== ""
      setIsProviderSelected(hasValidProvider)
    }
  }, [cell?.row?.original, index, newEntry, defaultEditMode])

  // Auto-save for non-clinical when all three fields (name, role_category, role_title) are filled
  React.useEffect(() => {
    if (!defaultEditMode || !newEntry || cell?.row?.index !== 1 || autoSavedRef.current) {
      return
    }

    // Add a small delay to ensure state updates complete
    const timeoutId = setTimeout(() => {
      const currentRowData = cell?.row?.original
      const resourceName = (currentRowData?.resource_name || "").trim()
      const roleCategory = currentRowData?.role_category || ""
      const roleTitle = (currentRowData?.role_title || "").trim()

      console.log("🔄 [Auto-save] Checking fields:", {
        resourceName,
        roleCategory,
        roleTitle,
        allFieldsFilled: resourceName && roleCategory && roleCategory !== "Select role" && roleTitle,
      })

      // Check if all three fields are filled
      const allFieldsFilled =
        resourceName &&
        roleCategory &&
        roleCategory !== "Select role" &&
        roleTitle

      if (!allFieldsFilled) {
        return
      }

      // Find the create action
      const createAction = actions.find(
        (act: any) =>
          act?.apiCall &&
          newEntry &&
          cell?.row?.index === 1,
      )

      if (!createAction) {
        console.log("🔄 [Auto-save] No create action found")
        return
      }

      // Update payload with all three fields
      const updatedPayload = {
        ...createAction.payload,
        provider: {
          ...currentRowData,
          resource_name: resourceName,
          role_category: roleCategory,
          role_title: roleTitle,
        },
      }

      // Validate
      if (createAction.test) {
        const testResult = createAction.test(updatedPayload)
        if (!testResult) {
          console.log("🔄 [Auto-save] Validation failed")
          return
        }
      }

      console.log("🔄 [Auto-save] All fields filled, triggering API call:", updatedPayload)
      autoSavedRef.current = true
      handleApiCall(
        createAction.apiCall,
        updatedPayload,
        createAction.setSaved,
        () => setLoading(actions.indexOf(createAction)),
        () => stopLoading(actions.indexOf(createAction)),
      )
    }, 150) // Small delay to ensure state updates complete

    return () => clearTimeout(timeoutId)
  }, [
    cell?.row?.original?.resource_name,
    cell?.row?.original?.role_category,
    cell?.row?.original?.role_title,
    defaultEditMode,
    newEntry,
    cell?.row?.index,
    actions,
  ])

  const alertHandler = (stateUpdateData: any) => {
    const { payload, apiCall, setSaved, refreshData } = stateUpdateData
    dispatch(
      setConfirmState({
        open: true,
        providerType: payload?.primaryIndex ? "Staff member" : "Provider",
        loading: false,
        provider: payload?.provider?.resource_name,
        close: () => dispatch(setConfirmClose({})),
        actionHandler: () => {
          return apiCall({
            ...payload,
            setSaved,
            refreshData,
            loading: (loadVal: boolean) => dispatch(setConfirmLoading(loadVal)),
          })
        },
      }),
    )
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        justifyContent: "space-between",
        minWidth: "100%",
        maxWidth: "220px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          maxWidth: "65%",
        }}
      >
        {newEntry ? (
          <Box
            sx={{
              display: "flex",
              flex: 1,
              alignItems: "center",
              position: "relative",
            }}
          >
            {defaultEditMode ? (
              <TableTextField
                cell={cell}
                dispatch={updateHandler}
                label={"Staff name"}
                index={"resource_name"}
                autoFocus
                onCommit={(value: string) => {
                  // The useEffect will handle the API call when all three fields are filled
                  // This onCommit is just for logging/debugging if needed
                  console.log("🔄 [onCommit] Name field updated:", value)
                }}
              />
            ) : isProviderSelected ? (
              // Show selected provider name (non-editable)
              <Typography
                sx={{
                  display: "flex",
                  flex: 1,
                  mr: "6px",
                  ml: "15px",
                  whiteSpace: "normal",
                  cursor: "default",
                  userSelect: "none",
                  color: "#1976d2",
                  fontWeight: "500",
                }}
              >
                {fieldValue}
              </Typography>
            ) : (
              // Show dropdown for provider selection
              <TableSelect
                cell={cell}
                index={index}
                label="Provider"
                selectIndex="resource_name"
                referenceData={referenceData}
                selectValues={selectValues}
                dispatch={updateHandler}
                newEntry={newEntry}
                onCancel={() => {
                  dispatch(cancelNewEntry())
                }}
                onProviderSelect={(selected) => {
                  setIsProviderSelected(!!selected)
                  // Force cell to exit edit mode after selection
                  if (selected) {
                    setTimeout(() => {
                      const table = cell?.getContext?.()?.table as any
                      if (table) {
                        table.setEditingCell(null)
                      }
                    }, 100)
                  }
                }}
              />
            )}
            {!isProviderSelected && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: "-20px",
                  left: "0",
                  color: "#ff9800",
                  fontSize: "10px",
                  fontWeight: "bold",
                }}
              >
                Select a provider to save
              </Typography>
            )}
          </Box>
        ) : (
          <Typography
            sx={{
              display: "flex",
              flex: 1,
              mr: "6px",
              ml: "15px",
              whiteSpace: "normal",
              cursor: "default",
              userSelect: "none",
            }}
          >
            {fieldValue}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {actions.map((action, actionIndex) => {
          const { apiCall, payload, Icon, iconStyles, enabled, setSaved } =
            action
          const { test, alert } = action

          // Enhanced validation for new entries
          const isUploadAction =
            Icon?.name?.includes("Upload") ||
            Icon?.name?.includes("Save") ||
            Icon?.name?.includes("Check") ||
            Icon?.name?.includes("Publish")
          const isDeleteAction = Icon?.name?.includes("Delete")

          const shouldValidateProvider =
            newEntry && !defaultEditMode && isUploadAction
          const hasValidProvider =
            isProviderSelected &&
            cell?.row?.original?.[index] &&
            cell?.row?.original?.[index] !== `Select Provider`

          // Hide upload button if no provider is selected for new entries
          // Show delete button for new entries
          const shouldShowUploadButton =
            (!shouldValidateProvider || hasValidProvider) && !newEntry
          const shouldShowDeleteButton = newEntry && isDeleteAction
          const shouldShowButton =
            shouldShowUploadButton || shouldShowDeleteButton

          if (action.hidden) {
            return null
          }


          // Auto-save for new entries when provider selected (no upload button needed)
          React.useEffect(() => {
            if (newEntry && isProviderSelected && !autoSavedRef.current) {
              autoSavedRef.current = true
              if (test && !test(payload)) return
              handleApiCall(
                apiCall,
                payload,
                setSaved,
                () => setLoading(actionIndex),
                () => stopLoading(actionIndex),
              )
            }
          }, [
            newEntry,
            isProviderSelected,
            isUploadAction,
            test,
            payload,
            apiCall,
            setSaved,
            actionIndex,
          ])

          // Hide upload/save buttons for new entry rows (auto-save handles it)
          const hideButtonForNewEntryUpload = newEntry && isUploadAction

          return (
            <IconButton
              disabled={actionState.disabled || isApiInProgress}
              key={`action-${actionIndex}`}
              sx={{
                color: colors.primaryBlue,
                p: 1,
                display:
                  enabled && shouldShowButton && !hideButtonForNewEntryUpload
                    ? "flex"
                    : "none",
                opacity: shouldValidateProvider && !hasValidProvider ? 0.5 : 1,
              }}
              onClick={() => {
                if (test && !test(payload)) return

                // Enhanced validation for provider selection
                if (shouldValidateProvider && !hasValidProvider) {
                  console.warn("Please select a provider before saving")
                  return
                }

                if (alert) {
                  if (!newEntry) {
                    return alertHandler({
                      payload,
                      setSaved,
                      apiCall,
                    })
                  }

                  // If there's a new entry and we're trying to delete it, just remove the row
                  if (newEntry && action.Icon?.name?.includes("Delete")) {
                    setSaved(cell?.row?.index)
                    return
                  }

                  return setSaved(cell?.row?.index)
                }

                // Use enhanced API call handler to prevent multiple simultaneous calls
                handleApiCall(
                  apiCall,
                  payload,
                  setSaved,
                  () => setLoading(actionIndex),
                  () => stopLoading(actionIndex),
                )
              }}
            >
              {actionState.state[actionIndex] ? (
                <CircularProgress size="1.3rem" />
              ) : (
                <Icon sx={iconStyles} />
              )}
            </IconButton>
          )
        })}
      </Box>
    </Box>
  )
}

export const TableFooter = ({ renderValue, col }: renderProps) => {
  const check = col !== "resource_name" ? true : false

  return (
    <Box
      sx={{
        height: "60px",
        display: "flex",
        alignItems: "center",
        justifyContent: check ? "center" : "normal",
        pl: check ? "" : "25px",
        backgroundColor: "#13254d",
        color: "#fff",
        m: -1,
        boxShadow: !check ? " #00000022 1.95px  0px 2.6px" : "",
      }}
    >
      {renderValue}
    </Box>
  )
}

export const TableCell = ({
  renderValue,
  col,
  formatType,
  subtitle,
}: renderProps) => {
  const check = col === "resource_name" ? false : true
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        pl: check ? "" : "16px",
        justifyContent: check ? "center" : "normal",
        alignItems: check ? "center" : "normal",
      }}
    >
      <Box>
        {formatType ? formatNumber(renderValue, formatType) : renderValue}
      </Box>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            fontSize: "10px",
            color: "#FFFFFF",
            textAlign: "center",
            marginTop: "2px",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  )
}

export const TableSelect = ({
  selectValues,
  cell,
  dispatch,
  label,
  index,
  selectIndex,
  referenceData,
  onProviderSelect,
  newEntry,
  onCancel,
  isRoleCategory = false,
}: tableSelectProps) => {
  const [searchKey, setSearchKey] = React.useState<string>("")
  const [isOpen, setIsOpen] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const hasSelectedRef = React.useRef(false) // Track if a selection was just made

  const filteredOptions = isRoleCategory
    ? React.useMemo(() => {
      // For role categories, filter by role_category field
      const validRoleCategories = selectValues?.filter((item: any) => {
        const roleCategory = item?.role_category?.trim() || ""
        return roleCategory.length > 0
      })

      // Filter by search key for role categories
      const key = searchKey.toLowerCase()
      return validRoleCategories?.filter((item: any) => {
        const roleCategory = (item?.role_category || "").toLowerCase()
        return roleCategory.includes(key)
      })
    }, [selectValues, searchKey])
    : referenceData
      ? React.useMemo(() => {
        // First, filter out providers with empty names (both firstname and lastname empty/null/undefined)
        const validNameProviders = selectValues?.filter((item: any) => {
          const firstName = item?.firstname?.trim() || ""
          const lastName = item?.lastname?.trim() || ""
          // Include only if at least one name field has a value
          return firstName.length > 0 || lastName.length > 0
        })

        // Filter already included provider
        const filteredState = validNameProviders?.filter((item: any) => {
          let includeProvider = true
          referenceData?.slice(1)?.forEach((datum: any) => {
            if (datum?.provider_sk === item?.provider_sk) {
              includeProvider = false
            }
          })
          return includeProvider
        })

        // Filter provider by search key
        const key = searchKey.toLowerCase()
        return filteredState?.filter((item: any) => {
          const firstName = (item?.firstname || "").toLowerCase()
          const lastName = (item?.lastname || "").toLowerCase()
          return firstName.includes(key) || lastName.includes(key)
        })
      }, [selectValues, referenceData, searchKey])
      : selectValues?.filter((item: any) => {
        // Also filter empty names when no referenceData
        const firstName = item?.firstname?.trim() || ""
        const lastName = item?.lastname?.trim() || ""
        return firstName.length > 0 || lastName.length > 0
      })

  const searchHandler = (e: any) => {
    setSearchKey(e.target.value)
  }

  const handleClose = () => {
    setIsOpen(false)
    // If this is a new entry and no provider was selected, cancel it
    // But only if we didn't just make a selection (to prevent closing before state updates)
    if (newEntry && onCancel && !hasSelectedRef.current) {
      // Use setTimeout to allow state updates to complete
      setTimeout(() => {
        const currentValue =
          cell?.row?.original?.[selectIndex ? selectIndex : index]
        const hasValidProvider =
          currentValue &&
          currentValue !== `Select ${label}` &&
          currentValue !== ""
        if (!hasValidProvider) {
          onCancel()
        }
      }, 100)
    }
    // Reset the selection flag after a short delay
    setTimeout(() => {
      hasSelectedRef.current = false
    }, 200)
  }

  const handleOpen = () => {
    setIsOpen(true)
  }

  // Handle Escape key in the dropdown
  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen && newEntry && onCancel) {
        const currentValue =
          cell?.row?.original?.[selectIndex ? selectIndex : index]
        const hasValidProvider =
          currentValue &&
          currentValue !== `Select ${label}` &&
          currentValue !== ""
        if (!hasValidProvider) {
          onCancel()
        }
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey)
      return () => {
        document.removeEventListener("keydown", handleEscapeKey)
      }
    }
  }, [isOpen, newEntry, onCancel, cell, selectIndex, index, label])

  return (
    <Select
      fullWidth
      margin="none"
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      sx={{
        height: "40px",
        fontSize: "14px",
        backgroundColor: "#FFFFFF",
        ".MuiOutlinedInput-notchedOutline": { border: 0 },
      }}
      onAnimationEnd={() => inputRef.current?.focus()}
      inputProps={{
        "aria-label": `select a ${label}`,
      }}
      displayEmpty
      IconComponent={ExpandMoreIcon}
      value={strFormat(
        cell?.row?.original?.[selectIndex ? selectIndex : index],
      )}
      onChange={(event) => {
        const selectedValue = event.target.value
        // Mark that we just made a selection to prevent premature cancellation
        hasSelectedRef.current = true

        dispatch({
          index: cell?.row.index,
          value: selectedValue,
          field: cell?.column.id,
          original: cell?.row.original,
        })
        // Notify parent component about provider selection
        if (onProviderSelect) {
          const isValidSelection =
            selectedValue &&
            selectedValue !== `Select ${label}` &&
            selectedValue !== ""
          onProviderSelect(isValidSelection ? selectedValue : null)
        }
        // Close dropdown after selection
        setIsOpen(false)
      }}
      MenuProps={{
        autoFocus: false,
        sx: {
          marginTop: "5px",
          padding: 0,
          maxHeight: "400px",
          width: "280px",
          minWidth: "280px",
        },
      }}
    >
      {(referenceData || isRoleCategory) && (
        <TextField
          value={searchKey}
          autoFocus
          fullWidth
          onKeyDown={(e) => e.stopPropagation()}
          inputRef={inputRef}
          sx={{
            position: "sticky",
            zIndex: 1500,
            backgroundColor: "#fff",
            top: "10px",
            mb: "5px",
            px: "10px",
          }}
          size="small"
          placeholder={
            isRoleCategory ? "Search a role category" : "Search a provider"
          }
          onChange={searchHandler}
        />
      )}
      <MenuItem
        disabled
        key={"select-default}"}
        sx={{
          my: "5px",
          mx: "10px",
          py: 1,
          width: "200px",
          borderRadius: "5px",
          fontSize: "14px",
          whiteSpace: "normal",
        }}
        value={`Select ${label}`}
      >
        {`Select ${label}`}
      </MenuItem>
      {filteredOptions.length === 0 ? (
        <Typography
          sx={{
            textAlign: "center",
            my: "20px",
          }}
        >
          {isRoleCategory ? "No role categories" : "No providers"}
        </Typography>
      ) : (
        filteredOptions.map((data: any, key: number) => {
          // For role categories, extract role_category field directly
          // For providers, use extractObject with firstname-lastname
          const displayName = isRoleCategory
            ? strFormat(data?.role_category || "")
            : extractObject({ data, index })
          // For role categories, use role_category as value
          // For providers, use provider_sk::name format
          const uniqueValue = isRoleCategory
            ? displayName
            : data?.provider_sk
              ? `${data.provider_sk}::${displayName}`
              : displayName
          return (
            <MenuItem
              key={
                isRoleCategory
                  ? `role-category-${key}`
                  : `role_cate-${data?.provider_sk || key}`
              }
              sx={{
                height: "40px",
                mx: "10px",
                my: "5px",
                borderRadius: "5px",
                fontSize: "14px",
                width: "200px",
                whiteSpace: "break-spaces",
              }}
              value={uniqueValue}
            >
              {displayName}
            </MenuItem>
          )
        })
      )}
    </Select>
  )
}

export const TableTextField = (props: tableTextFieldProps) => {
  const { cell, dispatch, label, index, autoFocus, onCommit } = props
  const [fieldState, setfieldState] = React.useState(cell.row.original?.[index])

  console.log({})

  const fieldChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setfieldState(e.target.value)
  }

  const resetState = () => {
    setfieldState(cell.row.original[index])
  }

  return (
    <TextField
      margin="none"
      placeholder={label}
      size="small"
      fullWidth
      sx={{
        display: "flex",
        flex: 1,
      }}
      value={fieldState}
      onChange={fieldChangeHandler}
      autoFocus={autoFocus}
      onBlur={(e) => {
        dispatch({
          index: cell.row.index,
          value: e.target.value,
          field: cell.column.id,
          original: cell.row.original,
          fieldFunc: resetState,
        })
        onCommit?.(e.target.value)
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          ; (e.target as HTMLInputElement).blur()
        }
      }}
    />
  )
}
